import { Graphics, Text, Container, Sprite, Texture, Assets } from 'pixi.js';
import { GlowFilter } from 'pixi-filters';
import { MovableEntity } from './base/MovableEntity';
import { tween } from '../core/TweenManager';
import { SHIPS, DRONE_TYPE_DISPLAY, type EntityType, type SelectionInfo, type DroneType } from '@astra/shared';
import { ShipGraphics, createSVGExplosion } from './ShipGraphics';

export interface DroneData {
  id: string;
  droneType: 'miner' | 'hauler' | 'fighter';
  ownerType: string;
  ownerId: string;
  homeStationId: string;
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  rotation: number;
  hull: number;
  maxHull: number;
  shield: number;
  maxShield: number;
  state: string;
  targetId?: string;
  warpPhase?: string;
  isRogue: boolean;
  shipDefId?: string;
  /** Drone level (1..4) from the level system. Defaults to 1 for any drone
   *  loaded from an older snapshot that didn't carry the field. */
  level?: number;
}

// ============================================
// SVG DRONE TEXTURE SYSTEM
// ============================================

const svgTextureCache: Map<string, Texture> = new Map();
const svgLoadingPromises: Map<string, Promise<Texture | null>> = new Map();

// Drone SVG metadata — all three SVGs are 191×190, pointing RIGHT
export const DRONE_SVG_META: Record<string, { width: number; height: number; baseScale: number }> = {
  drone_miner:   { width: 191, height: 190, baseScale: 0.48 },
  drone_hauler:  { width: 191, height: 190, baseScale: 0.48 },
  drone_fighter: { width: 191, height: 190, baseScale: 0.48 },
};

// Uniform sprite scale — drones are no longer tiered.
const DRONE_SPRITE_SCALE = 1.1;

// SVGs point RIGHT, game rotation 0 = RIGHT, so no offset needed
// (Ships needed -90° because game expected UP; drones use MovableEntity where rotation matches velocity)
const SVG_ROTATION_OFFSET = -Math.PI / 2;

async function loadDroneSVGTexture(key: string): Promise<Texture | null> {
  if (svgTextureCache.has(key)) return svgTextureCache.get(key)!;
  if (svgLoadingPromises.has(key)) return svgLoadingPromises.get(key)!;

  const loadPromise = (async () => {
    try {
      const url = `/assets/ships/${key}.svg`;
      const texture = await Assets.load<Texture>(url);
      if (texture) {
        svgTextureCache.set(key, texture);
        return texture;
      }
    } catch {
      // SVG not found — will use Graphics fallback
    }
    return null;
  })();

  svgLoadingPromises.set(key, loadPromise);
  return loadPromise;
}

export function getDroneSVGTexture(key: string): Texture | null {
  return svgTextureCache.get(key) || null;
}

/** Preload all drone SVGs — call during boot */
export async function preloadDroneSVGs(): Promise<void> {
  const keys = Object.keys(DRONE_SVG_META);
  await Promise.all(keys.map((k) => loadDroneSVGTexture(k)));
}

// ============================================
// COLORS & GLOW
// ============================================

// In-world sprite color per drone type. Sourced from the shared DRONE_TYPE_DISPLAY
// table so adding a new type is a single edit in shared/drones.ts — no scattered
// constants here.
const droneSpriteColor = (t: 'miner' | 'hauler' | 'fighter'): number =>
  DRONE_TYPE_DISPLAY[t as DroneType].spriteColor;

const ROGUE_TINT = 0xff2222;

// Uniform glow — drones are no longer tiered.
const DRONE_GLOW = { distance: 6, outerStrength: 1.5, innerStrength: 0.5 } as const;

// ============================================
// DRONE ENTITY
// ============================================

export class Drone extends MovableEntity {
  public readonly entityType: EntityType = 'bot';
  public get selectionRadius(): number { return this.shipDefId ? 40 : 22; }
  public readonly id: string;
  public get isHostile(): boolean { return this.isRogue || !!this.shipDefId; }
  public get isDummy(): boolean { return !!this.shipDefId; }

  public droneType: 'miner' | 'hauler' | 'fighter';
  public hull: number;
  public maxHull: number;
  public shield: number;
  public maxShield: number;
  /** Drone level (1..4). Synced from server DroneState.level via NetworkManager. */
  public level: number = 1;
  public droneState: string;
  /** Current AI target id (asteroid/harvestable when mining) — drives the mining laser visual. */
  public targetId: string;
  /** Warp phase ('none' | 'calculating' | 'traveling' | 'cooldown') — drives the warp animation. */
  public warpPhase: string;
  public isRogue: boolean;
  public ownerType: string;
  public ownerId: string;
  public homeStationId: string;
  public shipDefId: string;

  /** SVG sprite (null if using Graphics fallback or ShipGraphics for dummies) */
  private svgSprite: Sprite | null = null;
  private glowFilter: GlowFilter | null = null;
  /** Graphics fallback body (null if using SVG) */
  private body: Graphics | null = null;
  /** ShipGraphics used for dummy enemies to render at real ship size */
  private shipGraphics: ShipGraphics | null = null;
  private nameTag: Text;

  constructor(data: DroneData) {
    super();
    this.useInterpolationBuffer = true;
    this.useRotationSmoothing = true;

    this.id = data.id;
    this.droneType = data.droneType;
    this.hull = data.hull;
    this.maxHull = data.maxHull;
    this.shield = data.shield;
    this.maxShield = data.maxShield;
    this.level = data.level ?? 1;
    this.droneState = data.state;
    this.targetId = data.targetId ?? '';
    this.warpPhase = data.warpPhase ?? 'none';
    this.isRogue = data.isRogue;
    this.ownerType = data.ownerType;
    this.ownerId = data.ownerId;
    this.homeStationId = data.homeStationId;
    this.shipDefId = data.shipDefId ?? '';

    this.position.set(data.x, data.y);
    this.targetX = data.x;
    this.targetY = data.y;
    this.rotation = data.rotation;
    this.targetRotation = data.rotation;

    // Dummy enemies use real ship graphics; regular drones use drone SVG/fallback
    if (this.isDummy && this.shipDefId) {
      this.buildShipBody();
    } else {
      const svgKey = `drone_${this.droneType}`;
      const texture = getDroneSVGTexture(svgKey);
      if (texture) {
        this.buildSVGBody(texture, svgKey);
      } else {
        this.buildGraphicsBody();
      }
    }

    this.nameTag = new Text({
      text: this.getLabel(),
      style: {
        fontFamily: 'monospace',
        fontSize: 10,
        fill: (this.isRogue || this.isDummy) ? 0xff5555 : 0xaaaaaa,
        align: 'center',
      },
    });
    this.nameTag.anchor.set(0.5, 1);
    this.nameTag.y = -22;
    this.addChild(this.nameTag);
  }

  // ---------- SVG rendering ----------

  private buildSVGBody(texture: Texture, svgKey: string): void {
    const meta = DRONE_SVG_META[svgKey] || DRONE_SVG_META.drone_miner;
    const scale = meta.baseScale * DRONE_SPRITE_SCALE;
    const color = (this.isRogue || this.isDummy) ? ROGUE_TINT : droneSpriteColor(this.droneType);

    this.svgSprite = new Sprite(texture);
    this.svgSprite.anchor.set(0.5);
    this.svgSprite.scale.set(scale);
    this.svgSprite.rotation = SVG_ROTATION_OFFSET;

    this.glowFilter = new GlowFilter({
      distance: DRONE_GLOW.distance,
      outerStrength: DRONE_GLOW.outerStrength,
      innerStrength: DRONE_GLOW.innerStrength,
      color,
      quality: 0.3,
    });
    this.svgSprite.filters = [this.glowFilter];

    this.addChild(this.svgSprite);
  }

  // ---------- Ship rendering for dummies ----------

  private buildShipBody(): void {
    const shipDef = SHIPS[this.shipDefId];
    const size = shipDef?.size || 'medium';
    this.shipGraphics = new ShipGraphics(0, 0, this.shipDefId, {
      color: 0xff4444, // Red tint for hostile dummy
      size,
    });
    this.shipGraphics.generateThrustTextures();
    this.addChild(this.shipGraphics);
  }

  // ---------- Graphics fallback ----------

  private buildGraphicsBody(): void {
    this.body = new Graphics();
    this.addChild(this.body);
    this.drawGraphicsBody();
  }

  private drawGraphicsBody(): void {
    if (!this.body) return;
    this.body.clear();
    const baseColor = (this.isRogue || this.isDummy) ? ROGUE_TINT : droneSpriteColor(this.droneType);
    const r = 14;

    if (this.droneType === 'miner') {
      this.body.poly(this.hexPoints(r));
      this.body.fill({ color: baseColor, alpha: 0.9 });
      this.body.stroke({ width: 1.5, color: 0xffffff, alpha: 0.6 });
    } else if (this.droneType === 'hauler') {
      this.body.roundRect(-r, -r * 0.75, r * 2, r * 1.5, 4);
      this.body.fill({ color: baseColor, alpha: 0.9 });
      this.body.stroke({ width: 1.5, color: 0xffffff, alpha: 0.6 });
    } else {
      this.body.moveTo(r, 0);
      this.body.lineTo(-r * 0.8, -r * 0.7);
      this.body.lineTo(-r * 0.4, 0);
      this.body.lineTo(-r * 0.8, r * 0.7);
      this.body.closePath();
      this.body.fill({ color: baseColor, alpha: 0.95 });
      this.body.stroke({ width: 1.5, color: 0xffffff, alpha: 0.6 });
    }

    this.body.circle(0, 0, r + 4);
    this.body.stroke({ width: 1, color: baseColor, alpha: 0.4 });
  }

  private hexPoints(r: number): number[] {
    const pts: number[] = [];
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      pts.push(Math.cos(a) * r, Math.sin(a) * r);
    }
    return pts;
  }

  // ---------- Rogue state change ----------

  private applyRogueState(): void {
    const color = (this.isRogue || this.isDummy) ? ROGUE_TINT : droneSpriteColor(this.droneType);

    if (this.svgSprite && this.glowFilter) {
      // SVG mode: just update glow color
      this.glowFilter.color = color;
    } else {
      // Graphics fallback: redraw
      this.drawGraphicsBody();
    }

    this.nameTag.text = this.getLabel();
    this.nameTag.style.fill = this.isRogue ? 0xff5555 : 0xaaaaaa;
  }

  // ---------- Public API ----------

  private getLabel(): string {
    if (this.isDummy) {
      return `⚠ DUMMY ${this.shipDefId}`;
    }
    const prefix = this.isRogue ? '⚠ Rogue' : '';
    const levelTag = this.level > 1 ? ` [L${this.level}]` : '';
    return `${prefix} ${capitalize(this.droneType)}${levelTag}`.trim();
  }

  updateFromServer(data: Partial<DroneData>): void {
    if (data.x !== undefined || data.y !== undefined || data.velocityX !== undefined || data.velocityY !== undefined) {
      this.syncFromServer(data.x ?? this.targetX, data.y ?? this.targetY, data.velocityX, data.velocityY);
    }
    if (data.rotation !== undefined) this.targetRotation = data.rotation;
    if (data.hull !== undefined) this.hull = data.hull;
    if (data.maxHull !== undefined) this.maxHull = data.maxHull;
    if (data.shield !== undefined) this.shield = data.shield;
    if (data.maxShield !== undefined) this.maxShield = data.maxShield;
    if (data.state !== undefined) this.droneState = data.state;
    if (data.targetId !== undefined) this.targetId = data.targetId;
    if (data.warpPhase !== undefined) this.warpPhase = data.warpPhase;
    if (data.isRogue !== undefined && data.isRogue !== this.isRogue) {
      this.isRogue = data.isRogue;
      this.applyRogueState();
    }
    if (data.level !== undefined && data.level !== this.level) {
      this.level = data.level;
      this.nameTag.text = this.getLabel();
    }
  }

  update(delta: number): void {
    this.updatePosition(delta);
    this.updateRotation();
    this.nameTag.rotation = -this.rotation;
    if (this.shipGraphics) {
      // Show thrust flames when the dummy is moving
      const speed = Math.hypot(this.velocityX ?? 0, this.velocityY ?? 0);
      this.shipGraphics.thrusting = speed > 5;
      this.shipGraphics.update();
    }
  }

  /** Spawn explosion particles + flash, then auto-destroy after animation. */
  explode(): void {
    if (!this.parent) return;
    const parent = this.parent;
    const cx = this.x, cy = this.y;

    // Hide body + nametag
    if (this.svgSprite) this.svgSprite.alpha = 0;
    if (this.body) this.body.alpha = 0;
    if (this.shipGraphics) this.shipGraphics.setVisible(false);
    this.nameTag.visible = false;

    // Dummies: Asteroids-style explosion — real SVG fragments drift apart
    if (this.isDummy && this.shipDefId) {
      createSVGExplosion(parent, cx, cy, this.rotation, this.shipDefId, 0xff4444);
      return;
    }

    // Regular drones: small spark explosion
    const flash = new Graphics();
    flash.circle(0, 0, 22);
    flash.fill({ color: 0xffd066, alpha: 0.9 });
    flash.position.set(cx, cy);
    flash.zIndex = 80;
    parent.addChild(flash);
    tween({
      targets: flash,
      alpha: 0,
      duration: 300,
      onComplete: () => flash.destroy(),
    });

    const sparkColors = [0xffaa44, 0xff6644, 0xffdd66];
    const debrisCount = 12;
    for (let i = 0; i < debrisCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 40 + Math.random() * 80;
      const piece = new Graphics();
      const color = sparkColors[Math.floor(Math.random() * sparkColors.length)];
      piece.circle(0, 0, 1.5 + Math.random() * 2);
      piece.fill({ color, alpha: 1 });
      piece.position.set(cx, cy);
      piece.zIndex = 79;
      parent.addChild(piece);
      const tx = cx + Math.cos(angle) * speed;
      const ty = cy + Math.sin(angle) * speed;
      tween({
        targets: piece,
        x: tx,
        y: ty,
        alpha: 0,
        duration: 500 + Math.random() * 400,
        onComplete: () => piece.destroy(),
      });
    }
  }

  getSelectionInfo(): SelectionInfo {
    return {
      type: this.entityType,
      id: this.id,
      name: this.getLabel(),
      x: this.x,
      y: this.y,
      data: {
        droneType: this.droneType,
        hull: this.hull,
        maxHull: this.maxHull,
        shield: this.shield,
        maxShield: this.maxShield,
        isRogue: this.isRogue,
      },
    };
  }
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Re-export for callers that prefer a typed container.
export type { Container };
